import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { NIL as NIL_UUID } from 'uuid';
import {
  Component,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MAX_INT_32 } from '../constants/values';
import * as semver from 'semver';
import { API_ENDPOINTS } from '../constants/apiEnpoints';
@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  animations: [
    trigger('menuAnimation', [
      state(
        'hidden',
        style({
          opacity: 0,
          transform: 'scale(0.95)',
        })
      ),
      state(
        'visible',
        style({
          opacity: 1,
          transform: 'scale(1)',
        })
      ),
      transition('hidden => visible', animate('100ms ease-out')),
      transition('visible => hidden', animate('100ms ease-in')),
    ]),
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent implements ControlValueAccessor {
  private static readonly CLIENT_SIDE_SEARCH_IDENTIFIERS = new Set([
    'NonSchedulePracticeCode',
    'PracticeOperationalHoursPractice',
    'DentrixPracticeCode',
    'releaseMappingPracticeName',
    'PracticeTimezone',
  ]);
  @Input() loadScheduleData: any = [];
  @Input() nonScheduledPracticeData: any = [];
  private allOptions: any[] = [];

  @Input() options!: any;
  @Input() value: any = null;
  @Input() optionsURL!: string;
  @Input() dropDownJsonKey!: string;
  @Input() isRequired: boolean = false;
  @Input() disabled: boolean = false;
  @Input() uniqueIdentifier!: string;
  @Input() showNone: boolean = false;
  @Input() sortDir: 'desc' | 'asc' = 'asc';
  showDropdown: boolean = false;
  @Input() foreignKeyProperty!: string;
  @Output() valueChange = new EventEmitter<{
    id: string | NIL_UUID;
    name: string | null;
  }>();
  @Input() isSearchable: boolean = false;
  @Input() tabIndex!: number;
  searchValue: string = '';
  debounceTimer: any;
  @Input() dependentDropDownConfig: any;
  @Output() dependentDropDownChange = new EventEmitter<any>();
  @Input() id!: string;
  userEmail = localStorage.getItem('userEmail');
  isLoading: boolean = true;

  constructor(
    private _httpClient: HttpClient,
    private changeDetectorRef: ChangeDetectorRef,
    private eRef: ElementRef
  ) { }

  public get httpClient(): HttpClient {
    return this._httpClient;
  }
  public set httpClient(value: HttpClient) {
    this._httpClient = value;
  }

  ngOnInit() {
    if (this.uniqueIdentifier === 'LoadScheduleName') {
      this.loadScheduleData = [];
      this.changeDetectorRef.detectChanges();
    }
    if (this.uniqueIdentifier === 'NonSchedulePracticeCode') {
      this.nonScheduledPracticeData = [];
      this.changeDetectorRef.detectChanges();

      // Fetching all practice data
      this._httpClient
        .get<any>(`${API_ENDPOINTS.PRACTICES}`, {
          params: { pageSize: MAX_INT_32 },
        })
        .subscribe(
          (response) => {
            this.nonScheduledPracticeData = response.data;
            this.changeDetectorRef.detectChanges();
          });
    }
    this.fetchOptions();
    
    // Add capture phase listener to close dropdown before grid's handler
    document.addEventListener('pointerdown', this.handleDocumentClick, true);
  }

  onChange(selectedObject: any) {
    this.value = {
      id: selectedObject.id,
      name: selectedObject.name,
    };

    this.propagateChange(this.value);
    this.toggleDropdown();
    this.valueChange.emit(this.value);

    if (this.dependentDropDownConfig && selectedObject) {
      this.dependentDropDownChange.emit({
        key: this.dependentDropDownConfig.dependentDropDownKey,
        value: selectedObject.id,
      });
    }
    this.searchValue = '';
  }

  toggleDropdown() {
    if (this.disabled) {
      return;
    }
    this.showDropdown = !this.showDropdown;
  }

  private shouldUseClientSideSearch(): boolean {
    return (
      this.isSearchable &&
      this.allOptions.length > 0
    );
  }

  private applySearchToOptions(options: any[]): any[] {
    if (!this.searchValue) {
      return [...options];
    }

    const normalizedSearch = this.searchValue.toLowerCase();
    return options.filter((option: any) =>
      option?.name?.toLowerCase().includes(normalizedSearch)
    );
  }

  private setOptions(options: any[]): void {
    this.allOptions = [...options];
    this.options = this.applySearchToOptions(this.allOptions);
  }

  fetchOptions() {
    if (this.uniqueIdentifier === 'PMSMasterName') {
      const staticOptions = [
        { id: 1, name: 'ABELDent' },
        { id: 5, name: 'ClearDent' },
        { id: 4, name: 'Dentrix' },
        { id: 2, name: 'Power Practice' },
        { id: 3, name: 'Tracker' },
      ];

      this.setOptions(staticOptions);
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    } else if (this.uniqueIdentifier === 'DayOfWeek') {
      this.setOptions(this.options || []);
      if (this.value && typeof this.value === 'object' && this.value.name != null) {
        const matched = this.options?.find((o: any) => o.id == this.value.name || o.name == this.value.name);
        if (matched) this.value = { id: matched.id, name: matched.name };
      }
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    } else if (this.uniqueIdentifier === 'PracticeTimezone') {
      this._httpClient
        .get<any[]>('/assets/timezones.json')
        .subscribe(
          (data) => {
            const mappedOptions = data
              .map((item: any) => ({
                id: item.text,
                name: item.text,
              }))
              .sort((a, b) =>
                this.sortDir === 'asc'
                  ? a.name.localeCompare(b.name)
                  : b.name.localeCompare(a.name)
              );
            this.setOptions(mappedOptions);
            if (this.value && typeof this.value === 'string') {
              this.value = { id: this.value, name: this.value };
            }
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
          },
          (error) => {
            this.isLoading = false;
            console.error('Error fetching timezone options:', error);
          }
        );
    } else if (this.uniqueIdentifier === 'DentrixPracticeCode') {      this._httpClient
        .get<any>(this.optionsURL, { params: { pageSize: MAX_INT_32 } })
        .subscribe(
          (response) => {
            if (response.isSuccess && response.data) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false && item.pmsName === 'Dentrix')
                .map((item: any) => ({
                  id: item.id,
                  name: item.pmsName ? `${item.practiceCode} (${item.pmsName})` : item[this.dropDownJsonKey],
                }))
                .sort((a, b) =>
                  this.sortDir === 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            }
            this.isLoading = false;
            this.changeDetectorRef.detectChanges();
          },
          (error) => {
            this.isLoading = false;
            console.error('Error fetching options:', error);
          }
        );
    } else {
      if (!this.optionsURL) { this.isLoading = false; return; }
      this._httpClient
        .get<any>(this.optionsURL, {
          params: { pageSize: MAX_INT_32 },
        })
        .subscribe(
          (response) => {
            if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'LoadScheduleName'
            ) {
              this.loadScheduleData = response.data;
              this.changeDetectorRef.detectChanges();
            }

            if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'LoadScheduleName'
            ) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false)
                .map((item: any) => ({
                  id: item.id,
                  name: item[this.dropDownJsonKey],
                  sequenceId: item.sequenceId
                }))
                .sort((a, b) => (a.sequenceId || 0) - (b.sequenceId || 0));
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'PracticeOperationalHoursPractice'
            ) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false)
                .map((item: any) => ({
                  id: item.id,
                  name: item.pmsName ? `${item.practiceCode} (${item.pmsName})` : item.practiceCode,
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier !== 'UserRoleEmailDropdown' &&
              this.uniqueIdentifier !== 'PracticeMasterStatusDropDown' &&
              this.uniqueIdentifier !== 'PMSMasterName' &&
              this.uniqueIdentifier !== 'Version' &&
              this.uniqueIdentifier !== 'PracticeOperationalHoursPractice' &&
              this.uniqueIdentifier !== 'releaseMappingPracticeName' &&
              this.uniqueIdentifier !== 'LoadScheduleName'
            ) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false)
                .map((item: any) => ({
                  id: item.id,
                  name: item[this.dropDownJsonKey],
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'UserRoleEmailDropdown'
            ) {
              const mappedOptions = response.data
                .filter(
                  (item: any) =>
                    item[this.dropDownJsonKey] !== this.userEmail &&
                    item.isActive !== false
                )
                .map((item: any) => ({
                  id: item.id,
                  name: item[this.dropDownJsonKey],
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'PracticeMasterStatusDropDown'
            ) {
              const mappedOptions = response.data
                .filter(
                  (item: any) =>
                    item.source == 'Installer' && item.isActive !== false
                )
                .map((item: any) => ({
                  id: item.id,
                  name: item[this.dropDownJsonKey],
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'Version'
            ) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false)
                .map((item: any) => ({
                  id: item.id,
                  name: item[this.dropDownJsonKey],
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? semver.compare(
                      semver.coerce(a.name),
                      semver.coerce(b.name)
                    )
                    : semver.compare(
                      semver.coerce(b.name),
                      semver.coerce(a.name)
                    )
                );
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'DentrixPracticeCode'
            ) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false)
                .map((item: any) => ({
                  id: item.id,
                  name: `${item.practiceCode} (${item.pmsName})`,
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            } else if (
              response.isSuccess &&
              response.data &&
              this.uniqueIdentifier === 'releaseMappingPracticeName'
            ) {
              const mappedOptions = response.data
                .filter((item: any) => item.isActive !== false && item.isInstalled !== false)
                .map((item: any) => ({
                  id: item.id,
                  name: `${item.practiceCode} (${item.pmsName})`,
                }))
                .sort((a, b) =>
                  this.sortDir == 'asc'
                    ? a?.name?.localeCompare(b.name)
                    : b?.name?.localeCompare(a.name)
                );
              this.setOptions(mappedOptions);
            }
            else {
              console.error('Failed to fetch options:', response.errorMessage);
            }
            this.isLoading = false;
            // Trigger change detection
            this.changeDetectorRef.detectChanges();
          },
          (error) => {
            this.isLoading = false;
            console.error('Error fetching options:', error);
          }
        );
    }
  }

  filterOptions(event: any) {
    this.showDropdown = true;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (this.shouldUseClientSideSearch()) {
        this.options = this.applySearchToOptions(this.allOptions);
        this.changeDetectorRef.detectChanges();
        return;
      }

      this.fetchOptions();
    }, 200);
  }

  propagateChange = (_: any) => { };

  writeValue(value: any) {
    if (value !== undefined) {
      // Normalize plain string value to {id, name} for timezone dropdown
      if (this.uniqueIdentifier === 'PracticeTimezone' && value && typeof value === 'string') {
        this.value = { id: value, name: value };
      } else {
        this.value = value;
      }
      if (this.dependentDropDownConfig) {
        this.dependentDropDownChange.emit({
          key: this.dependentDropDownConfig.dependentDropDownKey,
          value: value?.id || null,
        });
      }
    }
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched() { }

  onDropdownPointerDown(event: Event): void {
    // Stop ALL propagation including other capture phase listeners
    event.stopImmediatePropagation();
  }

  handleDocumentClick = (event: Event): void => {
    if (!this.showDropdown) return;

    const target = event.target as HTMLElement;
    if (!this.eRef.nativeElement.contains(target)) {
      this.showDropdown = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    document.removeEventListener('pointerdown', this.handleDocumentClick, true);
  }
}
